import React, { useEffect, useState } from "react";
import DataTable, { TableColumn } from "react-data-table-component";
import { toast } from "react-toastify";
import { getAuthHeaders } from "../../utils/auth";
import "react-toastify/dist/ReactToastify.css";
import { apiAxios } from "../../utils/apiAxios";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

interface DelayReport {
  orderID: number;
  patientName: string;
  hubID: number;
  delayReason: string;
  reportedByName: string;
  reportedDate: string;
  expectedCompletionDate: string;
  status: string;
}

const ReportDelayPage: React.FC = () => {
  const [reports, setReports] = useState<DelayReport[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  const formatDateTimeWithTimezone = (dateString: string): { date: string, time: string } => {
    try {
      if (!dateString) return { date: "N/A", time: "N/A" };

      const date = new Date(dateString);
      const options: Intl.DateTimeFormatOptions = {
        timeZone: "Asia/Kolkata",
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      };

      const formattedDateTime = date.toLocaleString("en-IN", options);
      const [datePart, timePart] = formattedDateTime.split(", ");

      return { date: datePart, time: timePart || "N/A" };
    } catch {
      return { date: "N/A", time: "N/A" };
    }
  };

  const calculateDaysRemaining = (expected: string): number => {
    try {
      if (!expected) return 0;

      const expectedDate = new Date(expected);
      const today = new Date();

      const expectedIST = new Date(expectedDate.toLocaleString("en-US", { timeZone: "Asia/Kolkata" }));
      const todayIST = new Date(today.toLocaleString("en-US", { timeZone: "Asia/Kolkata" }));

      expectedIST.setHours(0, 0, 0, 0);
      todayIST.setHours(0, 0, 0, 0);

      const diffTime = expectedIST.getTime() - todayIST.getTime();
      return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    } catch {
      return 0;
    }
  };
  // FIXED: valid type for react-data-table-component

  const columns: TableColumn<DelayReport>[] = [
    {
      name: "Order ID",
      selector: (row) => `ORD-${row.orderID}`,
      sortable: true,
      width: "120px",
    },
    {
      name: "Patient Name",
      selector: (row) => row.patientName,
      sortable: true,
      width: "150px",
    },
    {
      name: "Delay Reason",
      selector: (row) => row.delayReason,
      wrap: true,

      style: {
        maxWidth: "250px",
        whiteSpace: "normal",
        wordBreak: "break-word",
      },
    },
    {
      name: "Reported By",
      selector: (row) => row.reportedByName,
      sortable: true,
      width: "180px",
    },
    {
      name: "Reported Date & Time",
      cell: (row) => {
        const { date, time } = formatDateTimeWithTimezone(row.reportedDate);
        return (
          <div>
            <div className="fw-medium">{date}</div>
            <small className="text-muted">{time}</small>
          </div>
        );
      },
      sortable: true,
      width: "190px",
      center: true,
    },
    {
      name: "Expected Completion",
      cell: (row) => {
        const { date, time } = formatDateTimeWithTimezone(row.expectedCompletionDate);
        return (
          <div>
            <div className="fw-medium">{date}</div>
            <small className="text-muted">{time}</small>
          </div>
        );
      },
      sortable: true,
      width: "190px",
      center: true,
    },
    {
      name: "Days Remaining",
      cell: (row) => {
        const days = calculateDaysRemaining(row.expectedCompletionDate);

        if (days < 0)
          return <span className="badge bg-warning">Overdue by {Math.abs(days)} days</span>;
        if (days === 0)
          return <span className="badge bg-warning">Due Today</span>;
        return <span className="badge bg-success">{days} days left</span>;
      },
      sortable: true,
      width: "180px",
      center: true,
    },
    {
      name: "Status",
      cell: (row) => (
        <span
          className={`badge ${row.status === "Resolved"
              ? "bg-success"
              : row.status === "In Progress"
                ? "bg-warning text-dark"
                : "bg-danger"
            }`}
        >
          {row.status}
        </span>
      ),
      sortable: true,
      width: "150px",
      center: true,
    },
  ];

const fetchDelayReports = async () => {
  try {
    setLoading(true);

    const { data } = await apiAxios.get<DelayReport[]>(
      `${API_BASE_URL}/Patient/DelayReports`
    );

    setReports(data);
  } catch (error: any) {
    console.warn("Error fetching reports:", error);

    const message =
      error?.response?.data?.message ||
      error?.message ||
      "Error fetching delay reports!";

    toast.error(message);
  } finally {
    setLoading(false);
  }
};
  useEffect(() => {
    fetchDelayReports();
  }, []);

  const customStyles = {
    headRow: {
      style: {
        // backgroundColor: "#3D8BFD",
        // color: "white",
        // borderTopLeftRadius: "8px",
        // borderTopRightRadius: "8px",
        fontSize: "15px", justifyContent: "center"
      },
    },

    headCells: {
      style: {
        fontSize: "14px",
        justifyContent: "center",
        fontWeight: 600,
      },
    },

    rows: {
      style: {
        minHeight: "52px",
      },
      // stripedStyle: {
      //   backgroundColor: "#eaeaea80", 
      // },
    },

    cells: {
      style: {
        fontSize: "14px",
      },
    },
  };

  return (
    <div className=" py-4">
      <h4 className="mb-4">Report Delay</h4>
      <div className="shadow-container rounded p-4">

        <DataTable
          columns={columns}
          data={reports}
          progressPending={loading}
          pagination
          highlightOnHover
          striped
          responsive
          customStyles={customStyles}

          // className="dentisttable-custom"



          noDataComponent={
            <div className="text-center py-5">
              <i className="bi bi-check-circle-fill text-success" style={{ fontSize: "48px" }}></i>
              <p className="mt-3 text-muted">No delay reports found</p>
              <p className="text-muted">All orders are running on time</p>
            </div>
          }
        />

      </div>

    </div>
  );
};

export default ReportDelayPage;
